import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { ChevronDownIconComponent, ChevronRightIconComponent } from '@itero/ui-toolkit-angular/icons';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { PonticSelectorComponent } from '@modules/tooth-editor/components/pontic-selector/pontic-selector.component';
import { EmptyStringAsHyphenPipe } from '@modules/treatment-information/pipes/empty-string-as-hyphen.pipe';
import { TreatmentInformationFacade } from '@modules/treatment-information/treatment-information.facade';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { IdName } from '@shared/models/id-name';
import { MapperPipe } from '@shared/pipes/mapper.pipe';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { fromUnitTypeInBridge, IntuitiveIndication } from '@modules/rx-intuitive-teeth-orchestrator/models/intuitive-indication';
import { isBridgeHeaderRow, isToothRow, type BridgeHeaderRow, type ToothRow, type TreatmentTableRow } from './treatment-table-row.model';

@Component({
	selector: 'rx-intuitive-treatment-table',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		MatTableModule,
		ChevronDownIconComponent,
		ChevronRightIconComponent,
		PonticSelectorComponent,
		MapperPipe
	],
	templateUrl: './rx-intuitive-treatment-table.component.html',
	styleUrls: ['./rx-intuitive-treatment-table.component.scss']
})
export class RxIntuitiveTreatmentTableComponent extends BaseDestroyableComponent {
	readonly displayedColumns: string[] = ['toothNumberLabel', 'treatment', 'specification', 'material', 'shadeBody'];

	private readonly collapsedBridges$ = new BehaviorSubject<Set<number>>(new Set<number>());

	private readonly allRows$ = combineLatest({
		standaloneTeeth: this.treatmentInformationFacade.treatments$,
		bridgesTeeth: this.treatmentInformationFacade.bridges$
	}).pipe(map(({ standaloneTeeth, bridgesTeeth }) => this.createTreatmentTableRows(standaloneTeeth, bridgesTeeth)));

	readonly displayedRows$: Observable<TreatmentTableRow[]> = combineLatest([this.allRows$, this.collapsedBridges$]).pipe(
		map(([rows, collapsedBridges]) =>
			rows
				.map(row => (isBridgeHeaderRow(row) ? { ...row, isCollapsed: collapsedBridges.has(row.bridgeIndex) } : row))
				.filter(row => this.isRowVisible(row, collapsedBridges))
		)
	);

	get ponticDesigns(): IdName[] {
		return this.treatmentInformationFacade.ponticDesigns ?? [];
	}

	readonly hasPonticDesignRow = (_index: number, row: TreatmentTableRow): boolean => isToothRow(row) && !!row.ponticDesignId;

	constructor(
		private readonly treatmentInformationFacade: TreatmentInformationFacade,
		private readonly translateService: TranslateService,
		private readonly emptyStringAsHyphenPipe: EmptyStringAsHyphenPipe
	) {
		super();
	}

	onBridgeHeaderClick(row: TreatmentTableRow): void {
		if (!isBridgeHeaderRow(row)) {
			return;
		}

		const collapsedBridges = new Set(this.collapsedBridges$.value);

		if (collapsedBridges.has(row.bridgeIndex)) {
			collapsedBridges.delete(row.bridgeIndex);
		} else {
			collapsedBridges.add(row.bridgeIndex);
		}

		this.collapsedBridges$.next(collapsedBridges);
	}

	getDesignById(ponticDesignId: number | undefined, designs: IdName[]): IdName | null {
		if (!ponticDesignId || !designs?.length) {
			return null;
		}
		return designs.find(p => p.Id == ponticDesignId) ?? null;
	}

	private createTreatmentTableRows(standaloneTeeth: Tooth[], bridgesTeeth: Tooth[][]): TreatmentTableRow[] {
		const standaloneTeethRows = standaloneTeeth
			.sort((a, b) => (a.ToothID ?? 0) - (b.ToothID ?? 0))
			.map(this.mapToothToTreatmentTableRow.bind(this));

		const bridgeTeethRows = bridgesTeeth.flatMap(bridge => {
			const teethInBridgeRows = bridge.map(this.mapToothToTreatmentTableRow.bind(this));

			const teethIdsInBridge = bridge.map(t => t.ToothID).filter((id): id is number => id !== null);
			const leftIndex = Math.min(...teethIdsInBridge);
			const rightIndex = Math.max(...teethIdsInBridge);
			const bridgeHeaderRow: BridgeHeaderRow = {
				// eslint-disable-next-line max-len
				toothNumberLabel: `${this.translateService.instant('TreatmentInformation.Bridge')} ${this.treatmentInformationFacade.getToothNumberByIdSync(leftIndex)} - ${this.treatmentInformationFacade.getToothNumberByIdSync(rightIndex)}`,
				isBridgeHeader: true,
				bridgeIndex: bridge[0].BridgeIndex as number,
				isCollapsed: false
			};

			const ponticDesignId = bridge.find(t => (t.PonticDesignID ?? 0) > 0)?.PonticDesignID;
			const lastPonticIndex = bridge.map(t => fromUnitTypeInBridge(t.ToothInBridgeTypeID)).lastIndexOf(IntuitiveIndication.Pontic);

			if (ponticDesignId && lastPonticIndex >= 0) {
				teethInBridgeRows[lastPonticIndex] = { ...teethInBridgeRows[lastPonticIndex], ponticDesignId };
			}

			return [bridgeHeaderRow, ...teethInBridgeRows];
		});

		return [...standaloneTeethRows, ...bridgeTeethRows];
	}

	private mapToothToTreatmentTableRow(tooth: Tooth): ToothRow {
		const specification = tooth.SpecificationId ? this.treatmentInformationFacade.getSpecificationNameSync(+tooth.SpecificationId) : '';
		const material = tooth.MaterialID ? this.treatmentInformationFacade.getMaterialNameSync(+tooth.MaterialID) : '';

		return {
			toothNumberLabel: this.treatmentInformationFacade.getToothNumberByIdSync(tooth.ToothID ?? 0),
			treatment: tooth.BridgeIndex
				? this.treatmentInformationFacade.getBridgeTreatment(tooth)
				: this.treatmentInformationFacade.getUnitType(tooth),
			specification: this.emptyStringAsHyphenPipe.transform(specification),
			material: this.emptyStringAsHyphenPipe.transform(material),
			shadeBody: this.treatmentInformationFacade.getShadeByTooth(false, {
				incisal: tooth.ShadeIncisal,
				body: tooth.ShadeBody,
				gingival: tooth.ShadeGingival
			}),
			bridgeIndex: tooth.BridgeIndex ?? undefined,
			isBridgeHeader: false
		};
	}

	private isRowVisible(row: TreatmentTableRow, collapsedBridges: Set<number>): boolean {
		if (isBridgeHeaderRow(row)) {
			return true;
		}

		return !row.bridgeIndex || !collapsedBridges.has(row.bridgeIndex);
	}
}
